'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { FaUser, FaSignOutAlt, FaSignInAlt, FaUserPlus } from 'react-icons/fa';
import Button from '@/components/ui/Button';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useLanguage } from '@/contexts/LanguageContext';
import { User } from '@/lib/api';

interface HeaderProps {
  isAuthenticated: boolean;
  user: User | null;
  onLogout: () => void;
  onCashIn: () => void;
  onCashOut: () => void;
}

const Header: React.FC<HeaderProps> = ({
  isAuthenticated,
  user,
  onLogout,
  onCashIn,
  onCashOut
}) => {
  const { t } = useLanguage();

  return (
    <header className="header">
      <div className="header-container">
        <div className="header-content">
          <div className="header-left">
            <div className="logo-section">
              <div className="logo-container">
                <Image
                  src="/logo.png"
                  alt="B BOLD Logo"
                  width={120}
                  height={40}
                  className="logo-image"
                  priority
                />
              </div>
            </div>
            <div className="mobile-language-icon">
              <LanguageSwitcher />
            </div>
          </div>

          <div className="header-right">
            {isAuthenticated ? (
              <>
                {/* Desktop User Info */}
                <div className="desktop-user-info">
                  <div className="user-avatar">
                    <span className="avatar-icon">👤</span>
                  </div>
                  <div className="user-details">
                    <p className="user-name">{user?.name || 'User'}</p>
                    <div className="user-stats">
                      <div className="stat-item">
                        <span className="stat-label">Balance:</span>
                        <span className="stat-value">{user?.balance || '0'} Ks</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Desktop Action Buttons */}
                <div className="desktop-action-buttons">
                  <Button
                    variant="casino"
                    size="sm"
                    onClick={onCashIn}
                    className="cash-in-btn"
                  >
                    <span className="btn-content">
                      <span className="btn-text">{t('cashin')}</span>
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onCashOut}
                    className="cash-out-btn"
                  >
                    <span className="btn-content">
                      <span className="btn-text">{t('cashout')}</span>
                    </span>
                  </Button>
                  <Link href="/profile">
                    <Button
                      variant="transparent"
                      size="sm"
                      className="header-profile-btn"
                    >
                      <span className="btn-content p-4">
                        <FaUser className="btn-icon" />
                      </span>
                    </Button>
                  </Link>
                </div>

                {/* Mobile User Info */}
                <div className="mobile-user-info">
                  <div className="mobile-user-details">
                    <p className="mobile-user-name">{user?.name || 'User'}</p>
                    <div className="mobile-user-balance">
                      <span className="mobile-balance-value">{user?.balance || '0'} Ks</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Desktop Auth Buttons */}
                <div className="desktop-auth-buttons">
                  <Link href="/login">
                    <Button variant="transparent" size="sm" className="header-login-btn">
                      <span className="btn-content">
                        <FaSignInAlt className="btn-icon" />
                        <span className="btn-text">{t('login')}</span>
                      </span>
                    </Button>
                  </Link>
                  <Link href="/register">
                    <Button
                      variant="transparent"
                      size="sm"
                      className="header-register-btn"
                    >
                      <span className="btn-content">
                        <FaUserPlus className="btn-icon" />
                        <span className="btn-text">{t('register')}</span>
                        <span className="btn-sparkle">✨</span>
                      </span>
                    </Button>
                  </Link>
                </div>

                {/* Mobile Auth Info */}
                <div className="mobile-auth-info">
                  <p className="mobile-guest-text">Guest User</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
